import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Upload, FileText, Archive, ArchiveRestore, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { DocumentViewer } from "@/components/document-viewer/DocumentViewer";

const fmtDate = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleString("tr-TR"); } catch { return "—"; } };

export default function SupplierPriceLists({ supplierId }) {
  const { can } = useAuth();
  const canEdit = can("suppliers", "edit");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [viewerFile, setViewerFile] = useState(null);
  const closeViewer = useCallback(() => setViewerFile(null), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/suppliers/${supplierId}/price-lists`, { params: { include_archived: showArchived } });
      setItems(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [supplierId, showArchived]);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("note", note);
      await api.post(`/suppliers/${supplierId}/price-lists`, fd);
      toast.success("Fiyat listesi yüklendi");
      setFile(null); setNote("");
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const toggleArchive = async (it) => {
    try {
      await api.patch(`/suppliers/${supplierId}/price-lists/${it.id}`, { archived: !it.archived });
      toast.success(it.archived ? "Fiyat listesi tekrar aktif edildi" : "Fiyat listesi arşivlendi");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openFile = async (it) => {
    try {
      const resp = await api.get(`/suppliers/${supplierId}/price-lists/${it.id}/download`, { responseType: "blob" });
      const contentType = it.content_type || resp.data.type || "application/octet-stream";
      setViewerFile({ blob: new Blob([resp.data], { type: contentType }), filename: it.original_filename, contentType });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Card className="bg-slate-900 border-slate-800 rounded-sm p-5 space-y-4" data-testid="price-lists-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-xs font-mono uppercase tracking-widest text-slate-500">Fiyat Listeleri ({items.length})</h3>
        {canEdit && (
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setShowArchived((v) => !v)} data-testid="price-lists-toggle-archived">
            <Archive className="w-3.5 h-3.5 mr-1" />{showArchived ? "Arşivi Gizle" : "Arşivdekileri Göster"}
          </Button>
        )}
      </div>

      {canEdit && (
        <div className="flex flex-wrap items-end gap-2 border border-slate-800 rounded-sm p-3" data-testid="price-list-upload-form">
          <div>
            <Label className="text-[10px] uppercase font-mono text-slate-500">Dosya (PDF / JPEG / JPG / PNG)</Label>
            <Input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="h-9 text-xs w-64"
              onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="price-list-file-input" />
          </div>
          <div className="flex-1 min-w-48">
            <Label className="text-[10px] uppercase font-mono text-slate-500">Not (örn: Liste üzerinden %35 iskonto)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} className="h-9 text-xs" placeholder="Açıklama…" data-testid="price-list-note-input" />
          </div>
          <Button size="sm" className="h-9" disabled={!file || uploading} onClick={upload} data-testid="price-list-upload-btn">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-3.5 h-3.5 mr-1" />Yükle</>}
          </Button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-500" /></div>
      ) : items.length === 0 ? (
        <div className="text-sm text-slate-500 py-6 text-center border border-dashed border-slate-800 rounded-sm" data-testid="price-lists-empty">
          {showArchived ? "Kayıt yok." : "Henüz fiyat listesi yüklenmedi."}
        </div>
      ) : (
        <div className="border border-slate-800 rounded-sm overflow-hidden">
          <table className="w-full text-sm" data-testid="price-lists-table">
            <thead className="bg-slate-950/60 border-b border-slate-800">
              <tr className="text-[10px] uppercase font-mono text-slate-500">
                {["Dosya", "Not", "Yükleme Tarihi", "Yükleyen", "Durum", ""].map((h) => <th key={h} className="text-left px-3 py-2">{h}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {items.map((it) => (
                <tr key={it.id} className={it.archived ? "opacity-60" : ""} data-testid={`price-list-row-${it.id}`}>
                  <td className="px-3 py-2">
                    <button onClick={() => openFile(it)} className="inline-flex items-center gap-1.5 text-blue-400 hover:text-blue-300 text-xs" data-testid={`price-list-open-${it.id}`}>
                      <FileText className="w-3.5 h-3.5 shrink-0" /><span className="underline underline-offset-2">{it.original_filename}</span>
                      <ExternalLink className="w-3 h-3" />
                    </button>
                    <span className="text-[10px] text-slate-500 ml-2">{(it.size / 1024).toFixed(0)} KB</span>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-300">{it.note || "—"}</td>
                  <td className="px-3 py-2 text-xs font-mono whitespace-nowrap">{fmtDate(it.created_at)}</td>
                  <td className="px-3 py-2 text-xs">{it.uploaded_by}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded-sm border ${it.archived ? "border-amber-700 text-amber-400 bg-amber-950/30" : "border-emerald-700 text-emerald-400 bg-emerald-950/30"}`}
                      data-testid={`price-list-status-${it.id}`}>
                      {it.archived ? "Arşiv" : "Aktif"}
                    </span>
                    {it.archived && it.archived_at && <div className="text-[10px] text-slate-500 mt-0.5">{fmtDate(it.archived_at)} · {it.archived_by}</div>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {canEdit && (
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => toggleArchive(it)} data-testid={`price-list-${it.archived ? "restore" : "archive"}-${it.id}`}>
                        {it.archived ? <><ArchiveRestore className="w-3.5 h-3.5 mr-1" />Tekrar Aktif Et</> : <><Archive className="w-3.5 h-3.5 mr-1" />Arşivle</>}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <DocumentViewer file={viewerFile} onClose={closeViewer} />
    </Card>
  );
}
